import { BlockCatalog, BlockKeys } from '@data/Simulations/pipeline/BlockCatalog';
import { Pipeline, PipelineExecutionResult } from '@data/Simulations/pipeline/Pipeline';
import '@data/Simulations/pipeline/blocks/DensityBlock';
import '@data/Simulations/pipeline/blocks/InvertBlock';
import '@data/Simulations/pipeline/blocks/SolarParamsBlock';
import type { SensorData } from 'Simulations/SensorPoint';
import { Panels } from '@gui/constants/Panels';
import usePersistentStore, { getGlobalState } from '@gui/store/persistent/persistentStore';
import useNotificationStore from '@gui/store/notification';
import { BlockParameters, NotificationType, PipelinePhase, SIM_SUN, SIM_SUN4D } from '@visualizer/types';
import { getGrid } from 'store/LoadingStore';
import useViewerStore, { getViewer } from 'store/viewerStore';
import { Controller } from './Controller';
import { LayerState } from '@gui/store/layerState';
import { ControllerManager } from './ControllerManager';
import { getDensityDefaultParams, getInvertDefaultParams } from '@gui/components/panels/Element';
import { getSolarParamsDefault } from '@gui/components/panels/Filter/BlockParameters/SolarParamsParameters';
//
class PipelineController extends Controller {
    private lastResult: PipelineExecutionResult | null = null;
    private lastPreResult: SensorData[] | null = null;
    private runListeners = new Set<() => void>();

    constructor() {
        super();
        this.setDefaultsToPreBlocks();
    }

    private getActiveLayerPipeline(): Pipeline {
        return getGrid().getActiveLayer().getPipeline();
    }

    public clearCacheAll(): void {
        getGrid().getActiveLayer().getPipeline().clearCacheAll();
    }

    protected updateStore(runAfter = true): void {
        const pipeline = this.getActiveLayerPipeline();
        const pipelineJSON = pipeline.toJSON();

        const store = usePersistentStore.getState();
        const prevExecutionCount = store.getActiveLayerState().pipeline.executionCount;

        store.updateCurrentLayerState({
            // Preserve UI-only counters when persisting pipeline JSON
            pipeline: {
                ...pipelineJSON,
                executionCount: prevExecutionCount,
            },
        });

        if (runAfter) {
            this.run();
        }
    }

    refreshFromStore(newStore: Record<string, LayerState>): void {
        for (const layerId in newStore) {
            if (layerId === 'mixed') continue;

            try {
                const layer = getGrid().searchLayerByUID(layerId); // strict lookup
                layer.getPipeline().fromJSON(newStore[layerId].pipeline);
            } catch {
                // Saved state contains a layer that isn't loaded -> skip
                continue;
            }
        }
        this.run();
    }

    public addBlock<T extends BlockParameters>(type: string, params: T) {
        const pipeline = this.getActiveLayerPipeline();
        const newBlock = BlockCatalog.createBlock(type, params);
        pipeline.addBlock(newBlock);

        this.updateStore();
        return newBlock.id;
    }

    public removeBlock(blockId: string) {
        const pipeline = this.getActiveLayerPipeline();
        pipeline.removeBlock(blockId);
        this.updateStore();
    }

    public moveBlock(blockId: string, newPosition: number) {
        const pipeline = this.getActiveLayerPipeline();
        pipeline.moveBlock(blockId, newPosition);
        this.updateStore();
    }

    public toggleBlock(blockId: string, status: boolean | undefined = undefined) {
        const pipeline = this.getActiveLayerPipeline();
        pipeline.toggleBlock(blockId, status);
        this.updateStore();
    }

    public updateBlockParams<T extends BlockParameters>(blockId: string, params: Partial<T>) {
        const pipeline = this.getActiveLayerPipeline();
        const block = pipeline.getBlock(blockId);
        if (block) {
            block.updateParams(params);
            this.updateStore();
        } else {
            throw new Error(`Block with ID ${blockId} not found.`);
        }
    }

    public getBlockParams(blockId: string) {
        const pipeline = this.getActiveLayerPipeline();
        const block = pipeline.getBlock(blockId);
        return block?.getParams();
    }

    public getBlock(blockId: string) {
        const pipeline = this.getActiveLayerPipeline();
        return pipeline.getBlock(blockId);
    }

    public getBlockIdByType(type: string): string | undefined {
        const pipeline = this.getActiveLayerPipeline();
        return pipeline.getBlocks().find((block) => block.type === type)?.id;
    }

    public getLastResult() {
        return this.lastResult;
    }

    public getLastPreResult() {
        return this.lastPreResult;
    }

    public onRun(cb: () => void): () => void {
        this.runListeners.add(cb);
        return () => this.offRun(cb);
    }

    public offRun(cb: () => void): void {
        this.runListeners.delete(cb);
    }

    private emitRun() {
        this.runListeners.forEach((cb) => {
            try {
                cb();
            } catch (e) {
                console.error('[PipelineController] onRun listener failed', e);
            }
        });
    }

    public run(): PipelineExecutionResult {
        const defaultsUpdated = this.setDefaultsToPreBlocks();
        if (defaultsUpdated) {
            this.updateStore(false);
        }

        const activeLayer = getGrid().getActiveLayer();
        const runPipelineAtPhase = (capturePhase?: PipelinePhase, suppressNotifications: boolean = false) =>
            activeLayer.runPipeline({
                suppressNotifications,
                capturePhase,
                octTree: getGrid().octTree!,
            });

        let pipelineResult = runPipelineAtPhase(undefined, false);
        let designSchemeBlocksChanged = false;
        // console.debug('[Pipeline] result: ', pipelineResult);

        const notifier = useNotificationStore.getState();
        pipelineResult.notifications?.forEach((n) => {
            if (n.show) notifier.showNotification(n.message, n.type);
        });
        pipelineResult.errors?.forEach((error) => {
            notifier.showNotification(error.error.message, NotificationType.Error);
        });

        useViewerStore.getState().setActiveSensorPoints(pipelineResult.data?.length ?? 0);
        usePersistentStore.getState().setPanel(
            {
                remappedMinMax: pipelineResult.executionContext.remappedMinMax,
                absoluteMinMax: pipelineResult.executionContext.absoluteMinMax,
                tmpMinMax: pipelineResult.outputMinMax,
            },
            Panels.OVERLAYS
        );

        // DesignSchemes:
        const designSchemeController = ControllerManager.getInstance().getDesignSchemeController();
        const isSolarSmartFilterRunning = getGlobalState().solarEnergyResources.solarMatching.isMatchingDemand;

        if (!isSolarSmartFilterRunning) {
            const designSchemeResult = runPipelineAtPhase(PipelinePhase.Regular, true);
            designSchemeBlocksChanged = designSchemeController.refreshDesignSchemes(designSchemeResult, activeLayer.getId());

            if (designSchemeBlocksChanged) {
                pipelineResult = runPipelineAtPhase(undefined, false);
            }
        }

        getViewer().updateSensors(pipelineResult);
        useViewerStore.getState().setActiveSensorPoints(pipelineResult.data.length);

        // Rays:
        this.lastResult = pipelineResult;
        this.lastPreResult = runPipelineAtPhase(PipelinePhase.Pre, true).data;

        // Hook to alert around that the pipeline has executed
        const store = usePersistentStore.getState();
        const prevCount = store.getActiveLayerState().pipeline.executionCount;

        store.updateCurrentLayerState({
            pipeline: {
                ...store.getActiveLayerState().pipeline,
                executionCount: prevCount + 1,
            },
        });
        this.emitRun();
        return pipelineResult;
    }

    private setDefaultsToPreBlocks(): boolean {
        let activeLayer;
        try {
            activeLayer = getGrid().getActiveLayer();
        } catch {
            return false;
        }

        // Apply defaults only once, on first execution:
        const executionCount = usePersistentStore.getState().getActiveLayerState().pipeline.executionCount;
        if (executionCount > 0) return false;

        // Get blocks:
        const pipeline = activeLayer.getPipeline();
        const simKind = activeLayer.getSimKind();
        const isSolarLayer = simKind === SIM_SUN || simKind === SIM_SUN4D;
        const invertBlock = pipeline.getBlocks().find((b) => b.type === BlockKeys.Invert);
        const densityBlock = pipeline.getBlocks().find((b) => b.type === BlockKeys.Density);
        const hideZeroValuesBlock = pipeline.getBlocks().find((b) => b.type === BlockKeys.HideZeroValues);
        const solarBlock = pipeline.getBlocks().find((b) => b.type === BlockKeys.SolarParameters);
        if (!invertBlock || !densityBlock || !hideZeroValuesBlock || !solarBlock) {
            throw new Error('Missing required pre-block(s): Invert, Density, Hide Zero Values or Solar Parameters');
        }

        // Get default params:
        const solarParams = getGlobalState().solarEnergyResources.solarParams;
        const normalizePercent = (value: number) => {
            const normalized = value > 1 ? value / 100 : value;
            return Math.max(0, Math.min(1, normalized));
        };
        const isCloudCoverOn = !!solarParams.cloudCoverage.on;
        const isDiffuseRadiationOn = !!solarParams.diffuseRadiation.on;

        // Update the blocks with defaults:
        invertBlock.updateParams(getInvertDefaultParams() as Partial<BlockParameters>);
        densityBlock.updateParams({ ...getDensityDefaultParams(), enabled: false } as Partial<BlockParameters>);
        hideZeroValuesBlock.updateParams({ enabled: false } as Partial<BlockParameters>);
        solarBlock.updateParams({
            ...getSolarParamsDefault(),
            enabled: isSolarLayer && (isCloudCoverOn || isDiffuseRadiationOn),
            isCloudCoverOn: isSolarLayer ? isCloudCoverOn : false,
            cloudCoverPercentage: isSolarLayer ? normalizePercent(solarParams.cloudCoverage.value ?? 0) : 0,
            isDiffuseRadiationOn: isSolarLayer ? isDiffuseRadiationOn : false,
            diffuseRadiationPercentage: isSolarLayer ? normalizePercent(solarParams.diffuseRadiation.value ?? 0) : 0,
        } as Partial<BlockParameters>);

        return true;
    }

    // private _setFirstActiveItem(pipelineResult:PipelineExecutionResult){
    //    let {x,y,z} =  usePersistentStore.getState().getActiveLayerState().simulationItems;
    //    const oldIndex = Grid.xyzToIndex(x, y, z);
    //    const oldSelected = Grid.sensorPoints[oldIndex];
    //    const currentID = oldSelected.getID();
    //    if (pipelineResult.data.find((sensor) => sensor.getID() === currentID)) {
    //        return;
    //    }
    //     const indexes = pipelineResult.data
    //         .map((sensor) => sensor.getIndex())
    //         .sort((a, b) => {
    //             const pa = Grid.sensorPoints[a].getCenter();
    //             const pb = Grid.sensorPoints[b].getCenter();

    //             // 1. Sort by distance to origin (0,0,0)
    //             const distA = pa.mag();
    //             const distB = pb.mag();

    //             return distA - distB;
    //         }).sort((a,b) => {
    //              const pa = Grid.sensorPoints[a].getCenter();
    //              const pb = Grid.sensorPoints[b].getCenter();
    //               return pb.z - pa.z;
    //         });

    //     const firstSensorIndex = pipelineResult.data.length > 0 ? indexes[0] ?? 0 : 0;
    //     [x, y, z] = Grid.indexToXYZ(firstSensorIndex);
    //     console.log(usePersistentStore.getState().getActiveLayerState().simulationItems);
    //     const store = usePersistentStore.getState();
    //     const prevItems = store.getActiveLayerState().simulationItems;

    //     store.updateCurrentLayerState({
    //         simulationItems: { ...prevItems, x, y, z },
    //     });
    // }
}

export default PipelineController;
